import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CheckCircle, Circle, Clock, AlertCircle, Loader2 } from 'lucide-react';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  ProgressBar,
  SectionReveal,
} from '@/design-system';
import type { RootState, AppDispatch } from '@/store';
import {
  fetchReadinessItems,
  toggleReadinessItem,
  bulkUpdateReadiness,
  READINESS_ITEM_POINTS,
  type FeatureReadinessItem,
} from '@/store/slices/featureReadinessSlice';

interface FeatureReadinessChecklistProps {
  featureId: string;
  currentScore: number;
  onScoreChange?: (newScore: number) => void;
}

const FeatureReadinessChecklist: React.FC<FeatureReadinessChecklistProps> = ({
  featureId,
  currentScore,
  onScoreChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  // Selectors
  const readinessItems = useSelector(
    (state: RootState) => state.featureReadiness.itemsByFeatureId[featureId] || []
  );
  const isLoading = useSelector((state: RootState) =>
    state.featureReadiness.loadingFeatureIds.includes(featureId)
  );
  const updatingItemIds = useSelector(
    (state: RootState) => state.featureReadiness.updatingItemIds
  );
  const error = useSelector((state: RootState) => state.featureReadiness.error);

  // Fetch readiness items on mount
  useEffect(() => {
    if (featureId && readinessItems.length === 0) {
      dispatch(fetchReadinessItems(featureId));
    }
  }, [dispatch, featureId, readinessItems.length]);

  // Handle toggle item
  const handleToggleItem = useCallback(
    async (item: FeatureReadinessItem) => {
      const newValue = !item.isCompleted;
      await dispatch(
        toggleReadinessItem({
          featureId,
          itemId: item.id,
          isCompleted: newValue,
        })
      );
      // Refetch to get updated score
      dispatch(fetchReadinessItems(featureId));
    },
    [dispatch, featureId]
  );

  // Handle bulk update (Complete All / Reset All)
  const handleBulkUpdate = useCallback(
    async (isCompleted: boolean) => {
      const items = Object.keys(READINESS_ITEM_POINTS).map((itemKey) => ({
        item: itemKey,
        isCompleted,
      }));
      await dispatch(bulkUpdateReadiness({ featureId, items }));
      // Refetch to get updated items
      dispatch(fetchReadinessItems(featureId));
    },
    [dispatch, featureId]
  );

  // Calculate score from loaded items
  const calculateScore = useCallback(() => {
    if (readinessItems.length === 0) return currentScore;
    return readinessItems.reduce((total, item) => {
      const itemMeta = READINESS_ITEM_POINTS[item.item];
      return total + (item.isCompleted && itemMeta ? itemMeta.points : 0);
    }, 0);
  }, [readinessItems, currentScore]);

  const getStatusColor = (score: number): 'emerald' | 'amber' | 'rose' => {
    if (score >= 80) return 'emerald';
    if (score >= 40) return 'amber';
    return 'rose';
  };

  const getStatusTextColor = (score: number) => {
    if (score >= 80) return 'text-accent-emerald';
    if (score >= 40) return 'text-accent-amber';
    return 'text-accent-rose';
  };

  const getStatusText = (score: number) => {
    if (score >= 80) return 'Shovel Ready';
    if (score >= 40) return 'In Refinement';
    return 'Not Ready';
  };

  const calculatedScore = calculateScore();
  const completedCount = readinessItems.filter((item) => item.isCompleted).length;
  const totalCount = Object.keys(READINESS_ITEM_POINTS).length;

  // Loading state
  if (isLoading && readinessItems.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-accent-cyan animate-spin" />
        <span className="ml-3 text-muted-foreground">Loading readiness items...</span>
      </div>
    );
  }

  // Error state
  if (error && readinessItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-10 h-10 text-accent-rose mb-3" />
        <p className="text-muted-foreground mb-4">{error}</p>
        <GlassButton
          variant="secondary"
          size="sm"
          onClick={() => dispatch(fetchReadinessItems(featureId))}
        >
          Retry
        </GlassButton>
      </div>
    );
  }

  // Get ordered items for display
  const orderedItems = Object.entries(READINESS_ITEM_POINTS).map(([key, meta]) => {
    const item = readinessItems.find((i) => i.item === key);
    return {
      key,
      label: meta.label,
      points: meta.points,
      item,
    };
  });

  return (
    <div className="space-y-6">
      {/* Score Summary */}
      <SectionReveal>
        <GlassCard variant="strong" padding="md">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-foreground mb-1">Readiness Score</h3>
              <p className="text-sm text-muted-foreground/60">
                Complete all items to make this feature shovel ready (≥80%)
              </p>
            </div>
            <div className="text-right">
              <div className={`text-3xl font-bold ${getStatusTextColor(calculatedScore)}`}>
                {calculatedScore}%
              </div>
              <div className={`text-sm font-medium ${getStatusTextColor(calculatedScore)}`}>
                {getStatusText(calculatedScore)}
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <ProgressBar
              value={calculatedScore}
              color={getStatusColor(calculatedScore)}
              size="lg"
              gradient
            />
            <div className="flex justify-between text-xs text-muted-foreground/60">
              <span>Not Ready (0-39%)</span>
              <span>In Refinement (40-79%)</span>
              <span>Shovel Ready (80-100%)</span>
            </div>
          </div>
        </GlassCard>
      </SectionReveal>

      {/* Checklist Items */}
      <SectionReveal delay={0.1}>
        <div className="space-y-3">
          {orderedItems.map(({ key, label, points, item }) => {
            const isCompleted = item?.isCompleted || false;
            const isUpdating = item ? updatingItemIds.includes(item.id) : false;

            return (
              <GlassCard
                key={key}
                variant="default"
                padding="sm"
                hover
                interactive
                className={isCompleted ? 'border-accent-emerald/30' : ''}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <button
                      onClick={() => item && handleToggleItem(item)}
                      disabled={isUpdating || !item}
                      className="flex-shrink-0 transition-transform hover:scale-110 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isUpdating ? (
                        <Loader2 className="w-5 h-5 text-accent-cyan animate-spin" />
                      ) : isCompleted ? (
                        <CheckCircle className="w-5 h-5 text-accent-emerald" />
                      ) : (
                        <Circle className="w-5 h-5 text-muted-foreground/60 hover:text-muted-foreground" />
                      )}
                    </button>

                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={`font-medium ${
                            isCompleted ? 'text-foreground' : 'text-foreground'
                          }`}
                        >
                          {label}
                        </span>
                        <GlassBadge variant="default" size="sm">
                          {points} pts
                        </GlassBadge>
                      </div>
                      {isCompleted && item?.completedAt && (
                        <p className="text-xs text-accent-emerald mt-1">
                          Completed {new Date(item.completedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </GlassCard>
            );
          })}
        </div>
      </SectionReveal>

      {/* Action Buttons */}
      <SectionReveal delay={0.2}>
        <div className="flex items-center justify-between pt-4">
          <div className="text-sm text-muted-foreground/60">
            {completedCount} of {totalCount} items completed
          </div>
          <div className="flex items-center gap-2">
            <GlassButton
              variant="secondary"
              size="sm"
              onClick={() => handleBulkUpdate(false)}
              disabled={isLoading}
            >
              Reset All
            </GlassButton>
            <GlassButton
              variant="primary"
              size="sm"
              onClick={() => handleBulkUpdate(true)}
              disabled={isLoading}
            >
              Complete All
            </GlassButton>
          </div>
        </div>
      </SectionReveal>
    </div>
  );
};

export default FeatureReadinessChecklist;
